/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, Suspense, useState } from 'react';
import { Box, Flex } from '@100mslive/roomkit-react';
import FullPageProgress from '../../components/FullPageProgress';
import { GridCenterView } from '../../components/gridView';
import PracticeBeamQuestion from './Practice/PracticeBeamQuestion';
import QuestionInfo from './QuestionInfo';
import VideoList from '../VideoList';
import { formatTime } from '../../utils/helper';
import CodeEditor from './components/portal/CodeEditor';
import { QUESTION_TYPES } from '../../common/constants';

/**
 * Beam component — streaming TTS with sentence flush + debounce fallback,
 * stable per-message state, and manual audio playback of returned bytes
 */
export const Beam = ({
    candidatePeer,
    form,
    timer,
    data,
    isPlaying,
    codeEditorForm,
    changeInCode,
    setIsSubmitModalOpen,
    isSubmitModalOpen,
    answerTime,
    allMessages,
}) => {
    let currentAudio = null;
    const lastPlayedRef = useRef({ audio: null, audioData: null });
    const [canPlayAudio, setCanPlayAudio] = useState(false);

    useEffect(() => {
        const enableAudioPlayback = () => {
            if (!canPlayAudio) {
                setCanPlayAudio(true);
                console.log('Audio playback enabled after simulated user interaction');
            }
        };

        enableAudioPlayback();
    }, []);

    const playAudio = async (url) => {
        try {
            if (currentAudio) {
                currentAudio.pause();
                currentAudio.currentTime = 0;
                currentAudio = null;
                console.log('Stopped any currently playing audio');
            }

            const audio = new Audio(url);
            currentAudio = audio;

            console.log(`Playing audio from URL: ${url}`);
            await new Promise((resolve, reject) => {
                audio.onended = resolve;
                audio.onerror = reject;
                audio.play().catch(reject);
            });

            console.log('Audio playback finished');
        } catch (error) {
            console.error('Error during audio playback:', error);
        }
    };

    const playAudioSequentially = async (audioUrls) => {
        for (const url of audioUrls) {
            if (url) await playAudio(url);
        }
    };

    useEffect(() => {
        const runAudioPlayback = async () => {
            if (!canPlayAudio || !isPlaying) return;

            const currentAudio = data?.audio;
            const currentAudioData = data?.audioData;

            const isAudioChanged = JSON.stringify(lastPlayedRef.current.audio) !== JSON.stringify(currentAudio);
            const isAudioDataChanged = lastPlayedRef.current.audioData !== currentAudioData;

            if (!isAudioChanged && !isAudioDataChanged) {
                console.log('Audio already played, skipping...');
                return;
            }

            lastPlayedRef.current = {
                audio: currentAudio,
                audioData: currentAudioData,
            };

            console.log('Running audio playback logic...');

            if (currentAudioData) {
                console.log('Playing synthesized speech for message:', data?.message);
                const blob = new Blob([currentAudioData], { type: 'audio/wav' });
                const url = URL.createObjectURL(blob);
                await playAudio(url);
            }

            if (currentAudio) {
                if (Array.isArray(currentAudio)) {
                    console.log('Playing audio files sequentially from array:', currentAudio);
                    await playAudioSequentially(currentAudio);
                } else {
                    console.log('Playing single audio file:', currentAudio);
                    await playAudio(currentAudio);
                }
            }
        };

        runAudioPlayback();
    }, [canPlayAudio, isPlaying, data?.audio, data?.audioData]);

    /** Helper: display text lines */
    const showText = (txt) => txt?.split('\n').map((line, i) => <div key={i}>{line}</div>);

    /** ---------- UI (unchanged) ---------- */
    if (data?.questionType === QUESTION_TYPES.CODING) {
        return (
            <Suspense fallback={<FullPageProgress />}>
                <Flex
                    css={{ width: '100%', position: 'relative', overflow: 'hidden', height: 'auto', flexGrow: '1' }}
                    direction={{ '@initial': 'row', '@md': 'column' }}
                    className="video_question_container"
                >
                    <Flex
                        css={{ size: '100%', flexDirection: 'column', position: 'relative' }}
                        className="video_container"
                        direction={{ '@initial': 'column', '@md': 'column' }}
                    >
                        <CodeEditor
                            form={codeEditorForm}
                            changeInCode={changeInCode}
                            setIsSubmitModalOpen={setIsSubmitModalOpen}
                            isSubmitModalOpen={isSubmitModalOpen}
                            lastQuestion={false}
                            isPlaying={isPlaying}
                            question=""
                        />
                    </Flex>
                    <div className="ask-coding-question-div-beam">
                        <div className="ask-question d-flex justify-content-end">
                            Time To Answer Remaining : {formatTime(answerTime) ?? '00:00'}
                        </div>
                        <div className="question-number">IncBot</div>
                        <div className="ask-question">
                            {isPlaying ? showText(data?.message) : answerTime > 0 ? showText(data?.displayText) : ''}
                        </div>
                    </div>
                    <div className="video-tile-container video-mini-tile">
                        <VideoList peers={candidatePeer} maxTileCount={1} isTile />
                    </div>
                </Flex>
            </Suspense>
        );
    }

    // Default non-coding view
    return (
        <Suspense fallback={<FullPageProgress />}>
            <Box css={{ width: '100%', height: 'calc(100% - 64px)' }}>
                <QuestionInfo form={form} timer={timer} />
                <div className="d-flex h-100 w-100 align-items-center justify-content-center">
                    <Flex css={{ height: 'calc(100% - 10vh + 22px)', width: '70%', margin: 'auto 20px' }}>
                        <GridCenterView peers={candidatePeer} maxTileCount={1} />
                    </Flex>
                    <PracticeBeamQuestion allMessages={allMessages} />
                </div>
            </Box>
        </Suspense>
    );
};
